// /reports/run/:reportId — runs a single registered report and shows the result.
//
// Server registers reports under `branch: 'scrap'` (16 of them — see
// server/src/modules/reports/scrap/index.ts). This page reads the metadata
// from `GET /api/v1/reports`, builds a date-range form, runs the report,
// renders a table, and offers a CSV export.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Play } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { Input } from '../../../components/form/Input';
import {
  useReportsList,
  useRunReport,
  type ReportInfo,
  type ReportTable,
} from '../useReports';
import {
  buildCsv,
  downloadCsv,
  formatCell,
  startOfMonthIsoDate,
  todayIsoDate,
} from '../reportRunnerUtils';
import styles from './ReportRunnerPage.module.css';

interface RunParams {
  date_from: string;
  date_to: string;
  limit?: string;
  as_of?: string;
}

const NEEDS_LIMIT = new Set(['cancellations', 'bacs_runs']);
const NEEDS_AS_OF = new Set(['invoices_outstanding']);

export function ReportRunnerPage(): JSX.Element {
  const navigate = useNavigate();
  const { reportId = '' } = useParams<{ reportId: string }>();
  const list = useReportsList();
  const reports: ReportInfo[] = list.data ?? [];
  const meta = useMemo(() => reports.find((r) => r.key === reportId), [reports, reportId]);

  const [params, setParams] = useState<RunParams>({
    date_from: startOfMonthIsoDate(),
    date_to: todayIsoDate(),
  });
  const [submittedParams, setSubmittedParams] = useState<RunParams | null>(null);

  // Reset form when navigating between reports.
  useEffect(() => {
    setParams({ date_from: startOfMonthIsoDate(), date_to: todayIsoDate() });
    setSubmittedParams(null);
  }, [reportId]);

  const runQuery = useRunReport(
    reportId,
    (submittedParams ?? {}) as Record<string, string>,
    !!reportId && !!submittedParams,
  );

  function setField<K extends keyof RunParams>(key: K, value: RunParams[K]) {
    setParams((p) => ({ ...p, [key]: value }));
  }

  function handleRun(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmittedParams({ ...params });
  }

  function handleExport() {
    if (!runQuery.data) return;
    const csv = buildCsv(runQuery.data as ReportTable);
    const fname = `${reportId || 'report'}-${todayIsoDate()}.csv`;
    downloadCsv(fname, csv);
  }

  const errorMsg =
    runQuery.error instanceof Error ? runQuery.error.message : null;
  const showLimit = NEEDS_LIMIT.has(reportId);
  const showAsOf = NEEDS_AS_OF.has(reportId);
  const result = runQuery.data;

  return (
    <div className={styles.page}>
      <PageHeader
        title={meta?.title ?? reportId}
        subtitle={meta?.description ?? 'Run report'}
        actions={
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/reports/collections')}>
            Back to Reports
          </Button>
        }
      />

      {!list.isLoading && !meta ? (
        <Card>
          <p className={styles.errorMsg}>
            Report <code>{reportId}</code> is not registered. Pick one from the
            list below.
          </p>
          <ul className={styles.reportList}>
            {reports.map((r) => (
              <li key={r.key}>
                <button
                  type="button"
                  className={styles.reportLink}
                  onClick={() =>
                    navigate(`/reports/collections/run/${encodeURIComponent(r.key)}`)
                  }
                >
                  <span className={styles.reportTitle}>{r.title}</span>
                  {r.description ? (
                    <span className={styles.reportDesc}>{r.description}</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {meta ? (
        <>
          <Card title="Parameters">
            <form onSubmit={handleRun} className={styles.form}>
              {showAsOf ? (
                <Input
                  label="As-of date"
                  variant="text"
                  value={params.as_of ?? ''}
                  onChange={(e) => setField('as_of', e.target.value)}
                  placeholder={todayIsoDate()}
                  helper="Snapshot date (YYYY-MM-DD). Defaults to today if blank."
                />
              ) : (
                <div className={styles.row}>
                  <label className={styles.dateField}>
                    <span>Date from</span>
                    <input
                      type="date"
                      value={params.date_from}
                      onChange={(e) => setField('date_from', e.target.value)}
                      required
                    />
                  </label>
                  <label className={styles.dateField}>
                    <span>Date to</span>
                    <input
                      type="date"
                      value={params.date_to}
                      onChange={(e) => setField('date_to', e.target.value)}
                      required
                    />
                  </label>
                </div>
              )}
              {showLimit ? (
                <Input
                  label="Row limit"
                  variant="number"
                  min={1}
                  max={1000}
                  value={params.limit ?? '500'}
                  onChange={(e) => setField('limit', e.target.value)}
                  helper="Maximum rows to return (1–1000)."
                />
              ) : null}
              <div className={styles.formActions}>
                <Button
                  type="submit"
                  variant="primary"
                  icon={Play}
                  loading={runQuery.isFetching}
                >
                  Run report
                </Button>
                {result ? (
                  <Button
                    type="button"
                    variant="secondary"
                    icon={Download}
                    onClick={handleExport}
                  >
                    Export CSV
                  </Button>
                ) : null}
              </div>
            </form>
          </Card>

          {runQuery.isError ? (
            <Card>
              <p className={styles.errorMsg}>
                {errorMsg ?? 'Failed to run report.'}
              </p>
            </Card>
          ) : null}

          {runQuery.isFetching && !result ? (
            <Card>
              <div className={styles.empty}>
                <Spinner />
              </div>
            </Card>
          ) : null}

          {result ? <ReportTableView table={result as ReportTable} /> : null}
        </>
      ) : null}
    </div>
  );
}

function ReportTableView({ table }: { table: ReportTable }): JSX.Element {
  if (table.rows.length === 0) {
    return (
      <Card title="Results">
        <p className={styles.empty}>No rows match the selected parameters.</p>
        {table.meta ? <Meta meta={table.meta} /> : null}
      </Card>
    );
  }
  return (
    <Card title={`Results (${table.rows.length} rows)`} flush>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              {table.columns.map((c) => (
                <th key={c.key} className={styles[`col_${c.type}`] ?? ''}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, idx) => (
              <tr key={idx}>
                {table.columns.map((c) => (
                  <td key={c.key} className={styles[`col_${c.type}`] ?? ''}>
                    {formatCell(row[c.key] ?? null, c.type)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {table.meta ? <Meta meta={table.meta} /> : null}
    </Card>
  );
}

function Meta({ meta }: { meta: Record<string, string | number | null> }): JSX.Element {
  const entries = Object.entries(meta);
  return (
    <ul className={styles.meta}>
      {entries.map(([k, v]) => (
        <li key={k}>
          <span className={styles.metaKey}>{k.replace(/_/g, ' ')}</span>
          <span className={styles.metaValue}>{v === null ? '—' : String(v)}</span>
        </li>
      ))}
    </ul>
  );
}
